import { useCallback, useEffect, useRef, useState } from 'react';
import { streamCrmChat, ChatTurn } from '@/api/ai/chat';
import { useExecuteCrmChatAction } from '@/api/ai/chatAction';

// Number of turns sent to the server — history is client-side only.
const HISTORY_LIMIT = 20;

export type ChatActionStatus = 'pending' | 'executing' | 'done' | 'failed' | 'rejected';

export interface ChatAction {
  tool: string;
  args: Record<string, unknown>;
  status: ChatActionStatus;
  display?: { contactName?: string };
}

/** A chat message as rendered in the UI — a regular turn or a write-action confirmation card. */
export interface ChatUiMessage extends ChatTurn {
  action?: ChatAction;
}

// What the model sees in the history instead of the visual confirmation card.
const ACTION_HISTORY_TEXT: Record<ChatActionStatus, string> = {
  pending: '[הפעולה ממתינה לאישור המשתמש]',
  executing: '[הפעולה ממתינה לאישור המשתמש]',
  done: '[המשתמש אישר והפעולה בוצעה בהצלחה]',
  failed: '[הפעולה נכשלה בביצוע]',
  rejected: '[המשתמש דחה את הפעולה]'
};

const toHistoryTurn = (message: ChatUiMessage): ChatTurn | null => {
  if (message.action) return { role: 'assistant', content: ACTION_HISTORY_TEXT[message.action.status] };
  return message.content ? { role: message.role, content: message.content } : null;
};

export const useCrmAiChat = () => {
  const [messages, setMessages] = useState<ChatUiMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { mutateAsync: executeAction } = useExecuteCrmChatAction();

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  useEffect(() => stop, [stop]);

  const reset = useCallback(() => {
    stop();
    setMessages([]);
    setError(null);
    setActiveTool(null);
  }, [stop]);

  const appendAssistantDelta = (delta: string) => {
    setMessages(prev => {
      const last = prev[prev.length - 1];
      // An action card ends the current assistant bubble — further text opens a new one.
      if (!last || last.role !== 'assistant' || last.action) {
        return [...prev, { role: 'assistant', content: delta }];
      }
      return [...prev.slice(0, -1), { ...last, content: last.content + delta }];
    });
  };

  const setActionStatus = (index: number, status: ChatActionStatus) => {
    setMessages(prev =>
      prev.map((message, i) =>
        i === index && message.action ? { ...message, action: { ...message.action, status } } : message
      )
    );
  };

  const approveAction = useCallback(
    async (index: number) => {
      const target = messages[index];
      if (!target?.action || target.action.status !== 'pending') return;

      setActionStatus(index, 'executing');
      try {
        await executeAction({ tool: target.action.tool, args: target.action.args });
        setActionStatus(index, 'done');
        setMessages(prev => [...prev, { role: 'assistant', content: 'הפעולה בוצעה בהצלחה ✅' }]);
      } catch (err) {
        setActionStatus(index, 'failed');
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'שגיאה בביצוע העדכון';
        setMessages(prev => [...prev, { role: 'assistant', content: `הפעולה לא בוצעה: ${message}` }]);
      }
    },
    [messages, executeAction]
  );

  const rejectAction = useCallback(
    (index: number) => {
      const target = messages[index];
      if (!target?.action || target.action.status !== 'pending') return;
      setActionStatus(index, 'rejected');
    },
    [messages]
  );

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      if (!content || isStreaming) return;

      setError(null);
      const history: ChatTurn[] = [...messages.map(toHistoryTurn).filter((turn): turn is ChatTurn => Boolean(turn)), { role: 'user', content }].slice(-HISTORY_LIMIT);
      setMessages(prev => [...prev, { role: 'user', content }, { role: 'assistant', content: '' }]);
      setIsStreaming(true);

      try {
        abortControllerRef.current = new AbortController();
        // Once a confirmation card is shown, any further model text in this stream is
        // dropped — the card itself is the last thing the user sees until they decide.
        let suppressText = false;
        await streamCrmChat(
          history,
          event => {
            switch (event.type) {
              case 'text':
                setActiveTool(null);
                if (!suppressText) appendAssistantDelta(event.delta);
                break;
              case 'tool_start':
                setActiveTool(event.name);
                break;
              case 'tool_end':
                setActiveTool(null);
                break;
              case 'confirm_request':
                setActiveTool(null);
                suppressText = true;
                setMessages(prev => [
                  ...prev,
                  {
                    role: 'assistant',
                    content: '',
                    action: { tool: event.tool, args: event.args, status: 'pending', display: event.display }
                  }
                ]);
                break;
              case 'error':
                setError(event.message);
                break;
              default:
                break;
            }
          },
          abortControllerRef.current.signal
        );
      } catch (err) {
        if (!(err instanceof Error && err.name === 'AbortError')) {
          setError(err instanceof Error ? err.message : 'שגיאה לא צפויה');
        }
      } finally {
        setIsStreaming(false);
        setActiveTool(null);
        abortControllerRef.current = null;
        // Drop empty assistant placeholders left by the stream (action cards stay).
        setMessages(prev => prev.filter(message => message.role !== 'assistant' || message.content || message.action));
      }
    },
    [messages, isStreaming]
  );

  return { messages, isStreaming, activeTool, error, send, stop, reset, approveAction, rejectAction };
};
